import { Injectable, inject } from '@angular/core';

import { TopNavigationConfig } from '@frontend/sports/common/client-config-data-access';
import { CountItem, takeTop } from '@frontend/sports/common/core/data-access/sport-model';
import { TopMenuContentItem } from '@frontend/sports/navigation/core/feature/models';
import { linkedTransferState, tapIfServer } from '@frontend/vanilla/ssr';
import { Observable, map, startWith } from 'rxjs';

import { NavigationItemsMapper } from '../navigation-items-mapper';
import { BaseNavigationItemsProvider } from './base-navigation-items.provider';

@Injectable({ providedIn: 'root' })
export class SportNavigationItemsProvider extends BaseNavigationItemsProvider {
    private navigationConfig = inject(TopNavigationConfig);
    private mapper = inject(NavigationItemsMapper);

    readonly sportItems$: Observable<TopMenuContentItem[]>;
    readonly navigationItemsTransferState = linkedTransferState<TopMenuContentItem[]>('sport-navigation-items', []);

    constructor() {
        super();

        this.sportItems$ = this.sportsCache.getSportList().pipe(
            map((items) => this.mapItems(items)),
            tapIfServer((items) => this.navigationItemsTransferState.set(items)),
            startWith(this.navigationItemsTransferState()),
        );
    }

    public getSportsItems(maxSports: number): Observable<TopMenuContentItem[]> {
        return this.sportsCache.getSportList().pipe(map((items) => this.mapItems(items, maxSports)));
    }

    private mapItems(items: CountItem[], maxSports?: number): TopMenuContentItem[] {
        maxSports = maxSports === undefined ? this.maxSports : maxSports;

        if (maxSports < 1) {
            return [];
        }

        if (!this.isV2Navigation) {
            const beforeSports = new Set(this.navigationConfig.beforeSports.map((sport) => sport.activeName));
            items = items.filter((item) => !beforeSports.has(item.id.toString()));
        }

        const topSports = takeTop(items, maxSports);

        return topSports.map((item, index) =>
            this.mapper.buildSportMenuItem({
                id: item.id,
                name: item.name,
                separator: this.hasSeparator(index, this.isV2Navigation),
                isVirtual: item.isVirtual,
            }),
        );
    }

    private get isV2Navigation() {
        return !!this.navigationConfig.topNavigationItems?.length;
    }

    private get maxSports() {
        return this.isV2Navigation ? this.navigationConfig.sportsConfig.topSportsCount : this.navigationConfig.maxSports;
    }

    private hasSeparator(index: number, isV2Navigation: boolean) {
        const isFirstItem = index === 0;

        return isV2Navigation ? this.navigationConfig.sportsConfig?.topSports.useBeforeSeparator && isFirstItem : isFirstItem;
    }
}
